// src/components/Proyectos/TareaChat.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Drawer,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Slide,
  Popover,
  Badge,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import dayjs from "dayjs";
import { socket } from "../../socket";
import {
  getTareasMensajes,
  postTareasMensaje,
  getTareasParticipantes,
  postTareasParticipante,
  deleteTareasParticipante,
  getResponsablesTodos,
  marcarMensajesLeidos,
} from "../../api/Proyectos/ProyectosTareasApi";
import { uploadDocumentoTarea } from "../../api/Tareas/documentosTareasApi";
import DocumentosTareas from "./DocumentosTareas";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const participantAbbr = (p) => {
  if (p?.abreviatura) return String(p.abreviatura).toUpperCase();
  if (!p?.username) return "?";
  const parts = String(p.username).split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
};

const TareaChat = ({ open, onClose, tarea }) => {
  const [tab, setTab] = useState(0);
  const [mensajes, setMensajes] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  const decoded = JSON.parse(atob(localStorage.getItem("token").split(".")[1]));
  const userId = decoded.userId;
  const username = decoded.username;
  const email = decoded.email;
  const abreviatura = decoded.abreviatura;

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [participantsAnchor, setParticipantsAnchor] = useState(null);

  // 📦 Carga inicial
  useEffect(() => {
    if (!tarea || !open) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const [msgs, parts, users] = await Promise.all([
          getTareasMensajes(tarea.id),
          getTareasParticipantes(tarea.id),
          getResponsablesTodos(),
        ]);
        const normalizedMsgs = msgs.map((m) => {
          const autorData = m.autor || m.Autor || {};
          if (!autorData.abreviatura && autorData.username) {
            const parts = autorData.username.split(" ").filter(Boolean);
            autorData.abreviatura =
              parts.length >= 2
                ? (parts[0][0] + parts[1][0]).toUpperCase()
                : parts[0][0].toUpperCase();
          }
          return { ...m, autor: autorData };
        });
        setMensajes(normalizedMsgs);
        setParticipantes(parts);
        setUsuarios(users);
      } catch (err) {
        console.error("❌ Error cargando chat:", err);
      } finally {
        setLoading(false);
        scrollToBottom();
      }
    };
    loadData();
  }, [tarea, open]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // 📡 Socket nuevo mensaje
  useEffect(() => {
    if (!tarea?.id) return;
    const handleNuevoMensaje = ({ tareaId, mensaje }) => {
      if (tareaId !== tarea.id) return;
      if (mensaje.AutorId === userId) return;
      setMensajes((prev) => [...prev, mensaje]);
      scrollToBottom();
    };
    socket.on("tareas-nuevo-mensaje", handleNuevoMensaje);
    return () => socket.off("tareas-nuevo-mensaje", handleNuevoMensaje);
  }, [tarea?.id, userId]);

  // ✅ Marcar mensajes leídos
  useEffect(() => {
    const marcarLeidos = async () => {
      if (open && tarea?.id) {
        try {
          await marcarMensajesLeidos(tarea.id);
          socket.emit("tareas-mensajes-leidos", { tareaId: tarea.id });
        } catch (err) {
          console.error("❌ Error marcando mensajes leídos:", err);
        }
      }
    };
    marcarLeidos();
  }, [open, tarea]);

  const handleSend = async () => {
    if (!nuevoMensaje.trim()) return;
    setIsSending(true);
    try {
      const msg = await postTareasMensaje(tarea.id, {
        Mensaje: nuevoMensaje.trim(),
      });
      msg.autor = { id: userId, username, email, abreviatura };
      setMensajes((prev) => [...prev, msg]);
      setNuevoMensaje("");
      scrollToBottom();
    } catch (err) {
      console.error("❌ Error enviando mensaje:", err);
    } finally {
      setIsSending(false);
    }
  };

  // 📎 Subida de archivos (drag + paste + input)
  const uploadFiles = async (files) => {
    if (!files.length) return;
    try {
      setIsSending(true);
      for (const file of files) {
        const uploaded = await uploadDocumentoTarea(
          file,
          tarea.id,
          "Otros",
          dayjs().format("YYYY-MM-DD")
        );
        const filePath =
          uploaded?.filePath?.replace("/uploads/", "") || `tareas/${file.name}`;
        const msg = await postTareasMensaje(tarea.id, {
          Mensaje: `📎 File uploaded: ${filePath}`,
        });
        msg.autor = { id: userId, username, abreviatura, email };
        setMensajes((prev) => [...prev, msg]);
      }
      socket.emit("tareas-documento-actualizado", { tareaId: tarea.id });
    } catch (err) {
      console.error("❌ Error subiendo archivo:", err);
    } finally {
      setIsSending(false);
    }
  };

  // 🧲 Drag & Drop + Pegar
  useEffect(() => {
    if (!open) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      if (e.target === document || e.target === window) setIsDragging(false);
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      uploadFiles(files);
    };
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const files = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile());
      if (files.length) uploadFiles(files);
    };

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("paste", handlePaste);
    };
  }, [open, tarea]);

  const colorByUser = (name) => {
    const palette = ["#42A5F5", "#66BB6A", "#FF7043", "#AB47BC", "#26A69A"];
    const index = name.charCodeAt(0) % palette.length;
    return palette[index];
  };

  const handleAddParticipant = async (userId) => {
    if (!userId) return;
    try {
      await postTareasParticipante(tarea.id, { UserId: parseInt(userId) });

      // 🔁 Refrescamos lista de participantes
      const updated = await getTareasParticipantes(tarea.id);
      setParticipantes(updated);

      setSnackbarMsg("Participant added successfully");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("❌ Error añadiendo participante:", err);
      setSnackbarMsg("Error adding participant");
      setSnackbarOpen(true);
    }
  };

  // ❌ Eliminar participante
  const handleRemoveParticipant = async () => {
    if (!participantToDelete) return;
    try {
      await deleteTareasParticipante(tarea.id, participantToDelete.id);

      setParticipantes((prev) =>
        prev.filter((p) => p.id !== participantToDelete.id)
      );

      setSnackbarMsg("Participant removed successfully");
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
    } catch (err) {
      console.error("❌ Error removing participant:", err);
      setSnackbarMsg("Error removing participant");
      setSnackbarOpen(true);
    }
  };

  if (!tarea) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "100vw", md: 620 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* 🧭 Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
          pb: 1.25,
          borderBottom: "1px solid #ddd",
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            Chat: {tarea.definicion || tarea.tarea}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }} noWrap>
            Project: {tarea.proyecto}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Participantes — franja bajo el título */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "#fafafa",
        }}
      >
        <Tooltip title="Participants">
          <IconButton
            size="small"
            onClick={(e) => setParticipantsAnchor(e.currentTarget)}
            sx={{ p: 0.5 }}
          >
            <Badge
              badgeContent={participantes.length}
              color="primary"
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.65rem",
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <GroupOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            cursor: "pointer",
          }}
          onClick={(e) => setParticipantsAnchor(e.currentTarget)}
        >
          {participantes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No participants yet — click to add
            </Typography>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {participantes.slice(0, 5).map((p, idx) => (
                <Tooltip key={p.id} title={p.username || ""}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: p.colorHex || colorByUser(p.username || "?"),
                      color: "#fff",
                      border: "2px solid #fff",
                      ml: idx === 0 ? 0 : -0.75,
                      zIndex: 5 - idx,
                    }}
                  >
                    {participantAbbr(p)}
                  </Avatar>
                </Tooltip>
              ))}
              {participantes.length > 5 && (
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: "#90a4ae",
                    color: "#fff",
                    border: "2px solid #fff",
                    ml: -0.75,
                  }}
                >
                  +{participantes.length - 5}
                </Avatar>
              )}
            </Box>
          )}
        </Box>

        <Tooltip title="Add participant">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => setParticipantsAnchor(e.currentTarget)}
          >
            <PersonAddAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(participantsAnchor)}
        anchorEl={participantsAnchor}
        onClose={() => setParticipantsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { width: 340, maxWidth: "90vw", p: 1.5, maxHeight: 420 },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Participants ({participantes.length})
        </Typography>

        {participantes.length === 0 ? (
          <Typography sx={{ color: "#888", fontSize: "0.85rem", mb: 1.5 }}>
            No participants yet.
          </Typography>
        ) : (
          <List dense sx={{ mb: 1, maxHeight: 240, overflowY: "auto" }}>
            {participantes.map((p) => (
              <ListItem
                key={p.id}
                sx={{
                  border: "1px solid #eee",
                  borderRadius: 1.5,
                  mb: 0.75,
                  px: 1,
                  py: 0.25,
                  bgcolor: "#fafafa",
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    sx={{ color: "#d32f2f" }}
                    onClick={() => {
                      setParticipantToDelete(p);
                      setOpenDeleteDialog(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: p.colorHex || colorByUser(p.username || "?"),
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      width: 32,
                      height: 32,
                    }}
                  >
                    {participantAbbr(p)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={p.username}
                  secondary={p.email}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                  secondaryTypographyProps={{
                    fontSize: "0.75rem",
                    color: "#666",
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1 }} />

        <TextField
          select
          fullWidth
          size="small"
          label="Add participant"
          SelectProps={{ native: true }}
          onChange={(e) => {
            if (!e.target.value) return;
            handleAddParticipant(e.target.value);
            e.target.value = "";
          }}
        >
          <option value=""></option>
          {usuarios
            .filter(
              (u) =>
                !Boolean(u.isAdmin) &&
                String(u.username || "").trim().toLowerCase() !== "admin" &&
                !participantes.some((p) => Number(p.id) === Number(u.id)),
            )
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
        </TextField>
      </Popover>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} centered>
        <Tab label="Mensajes" />
        <Tab label="Documentos" />
      </Tabs>

      {/* 🧵 MENSAJES */}
      {tab === 0 && (
        <Box
          ref={dropZoneRef}
          sx={{
            flex: 1,
            p: 2,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {isDragging && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.6)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "3px dashed #00C2A3",
                borderRadius: 2,
                color: "#fff",
                fontSize: "1.3rem",
                fontWeight: 600,
                backdropFilter: "blur(6px)",
              }}
            >
              📎 Drop or paste files to upload
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : mensajes.length === 0 ? (
            <Typography sx={{ color: "#888", mt: 2 }}>
              There are no messages yet. Start the conversation!
            </Typography>
          ) : (
            mensajes.map((m) => {
              const isMine = m.AutorId === userId;
              const userColor =
                m.autor?.colorHex || colorByUser(m.autor?.username || "?");
              return (
                <Box
                  key={m.Id}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexDirection: isMine ? "row-reverse" : "row",
                  }}
                >
                  <Avatar
                    sx={{
                      ml: isMine ? 1 : 0,
                      mr: isMine ? 0 : 1,
                      color: "#ffffffff",
                      fontWeight: 600,
                      border: isMine
                        ? "1px solid #2196F3"
                        : "1px solid transparent",
                      bgcolor: userColor,
                    }}
                  >
                    {m.autor?.abreviatura ||
                      m.autor?.username?.[0]?.toUpperCase() ||
                      "?"}
                  </Avatar>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: isMine ? "#E3F2FD" : "#F9F9F9",
                      border: `1px solid ${isMine ? "#90CAF9" : "#E0E0E0"}`,
                      maxWidth: "95%",
                      width: "65%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: userColor,
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        mb: 0.3,
                      }}
                    >
                      {m.autor?.username || "Usuario"}
                    </Typography>

                    <Box sx={{ mt: 0.5 }}>
                      {m.Mensaje.startsWith("📎 File uploaded:") ? (
                        (() => {
                          const filePath = m.Mensaje.replace(
                            "📎 File uploaded: ",
                            ""
                          ).trim();
                          const url = `${process.env.REACT_APP_API_URL}/uploads/${filePath}`;
                          const fileName = filePath.split("/").pop();
                          const isImage = /\.(png|jpe?g|gif|webp)$/i.test(
                            fileName
                          );
                          const isPdf = /\.pdf$/i.test(fileName);

                          if (isImage)
                            return (
                              <Box
                                component="img"
                                src={url}
                                alt={fileName}
                                sx={{
                                  maxWidth: "100%",
                                  borderRadius: 1,
                                  border: "1px solid #ddd",
                                  mt: 0.5,
                                  cursor: "pointer",
                                  transition: "transform 0.2s ease",
                                  "&:hover": { transform: "scale(1.05)" },
                                }}
                                onClick={() => window.open(url, "_blank")}
                              />
                            );

                          if (isPdf)
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  cursor: "pointer",
                                  mt: 0.5,
                                }}
                                onClick={() => window.open(url, "_blank")}
                              >
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    bgcolor: "#d32f2f",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  PDF
                                </Box>
                                <Typography
                                  sx={{
                                    color: "#1976d2",
                                    fontWeight: 600,
                                    textDecoration: "none",
                                  }}
                                >
                                  {fileName}
                                </Typography>
                              </Box>
                            );

                          return (
                            <Typography
                              sx={{
                                color: "#1976d2",
                                fontWeight: 600,
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() => window.open(url, "_blank")}
                            >
                              {fileName}
                            </Typography>
                          );
                        })()
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-line", color: "#333" }}
                        >
                          {m.Mensaje}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#777",
                        mt: 0.4,
                        display: "block",
                        textAlign: isMine ? "right" : "left",
                      }}
                    >
                      {dayjs(m.Fecha).format("DD/MM HH:mm")}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>
      )}

      {/* 📁 DOCUMENTOS */}
      {tab === 1 && (
        <Box sx={{ flex: 1, p: 2 }}>
          <DocumentosTareas id_Tarea={tarea.id} />
        </Box>
      )}

      {/* ✍️ INPUT MENSAJE */}
      {tab === 0 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton color="primary" component="label" disabled={isSending}>
            <AttachFileIcon />
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={6} // limita el crecimiento vertical
            minRows={1} // altura inicial cómoda
            placeholder="Escribe tu mensaje... (Ctrl+Enter para enviar)"
            size="small"
            value={nuevoMensaje}
            onChange={(e) => setNuevoMensaje(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                alignItems: "flex-start", // alinea el texto arriba
              },
            }}
          />{" "}
          <IconButton
            color="primary"
            disabled={isSending || !nuevoMensaje.trim()}
            onClick={handleSend}
          >
            <SendIcon />
          </IconButton>
        </Box>
      )}

      {/* 🗑️ Diálogo eliminar participante */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#d32f2f",
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f" }} /> Remove participant
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this participant from the task?
          </DialogContentText>
          {participantToDelete && (
            <Box
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                p: 1,
                border: "1px solid #ddd",
              }}
            >
              {participantToDelete.username} ({participantToDelete.email})
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveParticipant}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMsg.toLowerCase().includes("error") ? "error" : "success"
          }
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default TareaChat;
